import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import API_URL from '../config';
import './Login.css';

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Unlock flow
  const [locked, setLocked] = useState(false);
  const [lockedEmail, setLockedEmail] = useState('');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [unlockError, setUnlockError] = useState('');
  const [unlocking, setUnlocking] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  // Two-factor step (October 2026): set when the server answers the password with a code step.
  const [mfaStep, setMfaStep] = useState(null); // { mfa: 'setup'|'verify', mfaToken, qr?, secret? }
  const [code, setCode] = useState('');
  const [trust, setTrust] = useState(true);
  const [backupCodes, setBackupCodes] = useState(null);
  const [pending, setPending] = useState(null); // login response held while backup codes are shown
  const deviceKey = e => `mfaDevice:${String(e || '').toLowerCase()}`;
  const readDevice = e => { try { return localStorage.getItem(deviceKey(e)) || ''; } catch { return ''; } };
  const finish = data => {
    if (data.deviceToken) { try { localStorage.setItem(deviceKey(email), data.deviceToken); } catch { /* private window */ } }
    localStorage.setItem('token', data.token);
    onLogin(data.user);
  };

  useEffect(() => {
    if (locked) {
      axios.get(`${API_URL}/api/auth/secret-question`)
        .then(r => setQuestion(r.data.question))
        .catch(() => setQuestion('What is your secret answer?'));
    }
  }, [locked]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password, deviceToken: readDevice(email) });
      if (response.data.mfa) {
        setMfaStep(response.data);
        setCode('');
        return;
      }
      localStorage.setItem('token', response.data.token);
      onLogin(response.data.user);
    } catch (err) {
      const data = err.response?.data;
      if (data?.locked) {
        setLockedEmail(data.email || email);
        setLocked(true);
      } else {
        setError(data?.error || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUnlock = async (e) => {
    e.preventDefault();
    setUnlockError('');
    setUnlocking(true);
    try {
      await axios.post(`${API_URL}/api/auth/unlock`, { email: lockedEmail, answer });
      setUnlocked(true);
      setAnswer('');
      // After 1.5s return to login form
      setTimeout(() => {
        setLocked(false);
        setUnlocked(false);
        setUnlockError('');
        setPassword('');
        setError('Account unlocked. Please log in.');
      }, 1500);
    } catch (err) {
      setUnlockError(err.response?.data?.error || 'Incorrect answer. Try again.');
      // Fetch a new random question on wrong answer
      axios.get(`${API_URL}/api/auth/secret-question`)
        .then(r => setQuestion(r.data.question))
        .catch(() => {});
    } finally {
      setUnlocking(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/mfa/verify`, { mfaToken: mfaStep.mfaToken, code, trustDevice: trust });
      if (r.data.backupCodes) { setBackupCodes(r.data.backupCodes); setPending(r.data); return; }
      finish(r.data);
    } catch (err) {
      const data = err.response?.data;
      if (data?.restart) { setMfaStep(null); setPassword(''); }
      setError(data?.error || 'Could not check the code.');
    } finally {
      setLoading(false);
    }
  };

  if (backupCodes) {
    return (
      <div className="login-container">
        <div className="login-box">
          <img src="/logo.png" alt="Mountain Recruiting" className="login-logo" />
          <h1>Save your backup codes</h1>
          <p className="mfa-text">If you lose your phone, each of these codes signs you in once. Save them somewhere safe (a password manager or printed). They won't be shown again.</p>
          <div className="mfa-codes">{backupCodes.map(c => <code key={c}>{c}</code>)}</div>
          <button type="button" onClick={() => { try { navigator.clipboard.writeText(backupCodes.join('\n')); } catch { /* ignore */ } }} className="mfa-secondary">Copy codes</button>
          <button type="button" onClick={() => finish(pending)}>I've saved them, continue</button>
        </div>
      </div>
    );
  }

  if (mfaStep) {
    const setup = mfaStep.mfa === 'setup';
    return (
      <div className="login-container">
        <div className="login-box">
          <img src="/logo.png" alt="Mountain Recruiting" className="login-logo" />
          <h1>{setup ? 'Set up two-factor sign-in' : 'Enter your code'}</h1>
          {setup ? (
            <>
              <p className="mfa-text">Scan this with your authenticator app (Google Authenticator, Microsoft Authenticator or Authy), then enter the 6-digit code it shows.</p>
              <img src={mfaStep.qr} alt="QR code for your authenticator app" className="mfa-qr" />
              <p className="mfa-text small">Can't scan? Add it by hand with this key: <code>{mfaStep.secret}</code></p>
            </>
          ) : (
            <p className="mfa-text">Open your authenticator app and enter the 6-digit code for Mountain CRM. Lost your phone? Enter one of your backup codes.</p>
          )}
          <form onSubmit={submitCode}>
            <div className="form-group">
              <label htmlFor="mfa-code">{setup ? '6-digit code' : 'Code'}</label>
              <input id="mfa-code" value={code} onChange={e => setCode(e.target.value)} inputMode={setup ? 'numeric' : 'text'} autoComplete="one-time-code" autoFocus required placeholder={setup ? '123 456' : '123 456 or backup code'} />
            </div>
            <label className="mfa-trust"><input type="checkbox" checked={trust} onChange={e => setTrust(e.target.checked)} /> Trust this device for 30 days</label>
            {error && <div className="error-message">{error}</div>}
            <button type="submit" disabled={loading}>{loading ? 'Checking...' : setup ? 'Turn on two-factor' : 'Sign in'}</button>
            <button type="button" className="mfa-secondary" onClick={() => { setMfaStep(null); setError(''); setPassword(''); }}>Back</button>
          </form>
        </div>
      </div>
    );
  }

  if (locked) {
    return (
      <div className="login-container">
        <div className="login-box">
          <img src="/logo.png" alt="Mountain Recruiting" className="login-logo" />
          <h1>Account Locked</h1>
          <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24, textAlign: 'center' }}>
            Answer the security question to unlock <strong>{lockedEmail}</strong>.
          </p>
          {unlocked ? (
            <div className="error-message" style={{ background: '#dcfce7', color: '#166534', borderColor: '#86efac' }}>
              ✓ Account unlocked! Redirecting…
            </div>
          ) : (
            <form onSubmit={handleUnlock}>
              <div className="form-group">
                <label>Security Question</label>
                <p style={{ fontSize: 14, color: '#1e2d5a', fontWeight: 600, margin: '4px 0 0' }}>{question || '…'}</p>
              </div>
              <div className="form-group">
                <label>Your Answer</label>
                <input
                  type="text"
                  value={answer}
                  onChange={e => setAnswer(e.target.value)}
                  required
                  placeholder="Enter your answer"
                  autoFocus
                />
              </div>
              {unlockError && <div className="error-message">{unlockError}</div>}
              <button type="submit" disabled={unlocking}>
                {unlocking ? 'Verifying…' : 'Unlock Account'}
              </button>
              <button
                type="button"
                onClick={() => { setLocked(false); setUnlockError(''); setAnswer(''); }}
                style={{ marginTop: 10, background: 'none', border: '1px solid #e2e8f0', color: '#64748b', width: '100%', padding: '10px', borderRadius: 6, cursor: 'pointer', fontSize: 14 }}
              >
                ← Back to Login
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <img src="/logo.png" alt="Mountain Recruiting" className="login-logo" />
        <h1>Access Your Recruiting Dashboard</h1>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
          <div style={{ textAlign: 'right', marginTop: 10 }}>
            <Link to="/forgot-password" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, textDecoration: 'none' }}>
              Forgot password?
            </Link>
          </div>
        </form>
        <div className="login-info">
          <p>Don't have an account?</p>
          <p>Reach out to us at <a href="mailto:hiring@mountainrecruiting.com">hiring@mountainrecruiting.com</a></p>
        </div>
      </div>
    </div>
  );
}

export default Login;
